/**
 * バイアススコアゲージコンポーネント
 *
 * バイアススコアを円形ゲージで可視化（低いほど公平）
 */

import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { border } from '../../../../utils/darkModeAdapter';

/**
 * バイアススコアゲージ
 * @param {number} score - バイアススコア（0-100、低いほど公平）
 * @param {string} label - ラベル
 */
export default function BiasScoreGauge({ score = 0, label = 'バイアススコア' }) {
  const theme = useTheme();

  // スコアに応じた色を決定（低いほど良い）
  const getColor = (value) => {
    if (value <= 20) return '#22c55e'; // green
    if (value <= 50) return '#f59e0b'; // amber
    return '#ef4444'; // red
  };

  // スコアの説明
  const getDescription = (value) => {
    if (value <= 20) return '非常に公平';
    if (value <= 40) return '概ね公平';
    if (value <= 60) return '注意が必要';
    if (value <= 80) return '要改善';
    return '深刻なバイアス';
  };

  const color = getColor(score);
  const description = getDescription(score);

  // 逆転した進捗（100 - score で「公平度」を表示）
  const fairnessScore = 100 - score;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 2
      }}
    >
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        {/* 背景の円 */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={120}
          thickness={4}
          sx={{ color: border(theme).subtle }}
        />
        {/* スコアの円 */}
        <CircularProgress
          variant="determinate"
          value={fairnessScore}
          size={120}
          thickness={4}
          sx={{
            color: color,
            position: 'absolute',
            left: 0,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            }
          }}
        />
        {/* 中央のテキスト */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h4"
            component="div"
            sx={{ fontWeight: 'bold', color }}
          >
            {score}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            /100
          </Typography>
        </Box>
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 1, textAlign: 'center' }}>
        {label}
      </Typography>

      <Typography
        variant="body2"
        sx={{ color, fontWeight: 600, textAlign: 'center' }}
      >
        {description}
      </Typography>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
        ※ 低いほど公平な評価
      </Typography>
    </Box>
  );
}
